import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { get, last, split } from 'lodash';
import { axiosService } from './axiosService';
import { useCreateQuery } from '../CRUD/useCreateQuery';
import { createKeys } from '../CRUD/createKeys';

interface TDownloadFileParams {
  url: string;
  fileName?: string;
  isEnabled?: boolean;
  onSuccess?: (params: unknown) => unknown;
  onError?: (params: unknown) => unknown;
  onSettled?: (params: unknown) => unknown;
  fileType?: string;
}

const normalizeFiles = (
  response: unknown,
  fileName?: string,
  fileType?: string,
) => {
  // get the type from the file to be downloaded
  const type =
    get(response, 'data.type') && last(split(get(response, 'data.type'), '/'));
  const fileUrl = (window.URL || window.webkitURL).createObjectURL(
    new Blob([get(response, 'data')], {
      type: fileType || get(response, 'data.type', ''),
    }),
  );
  const anchorTag = document.createElement('a');

  // keeping compatibility with fileName and fileType properties
  // in order to presert file type when downloading skip both properties when calling this function
  anchorTag.setAttribute('href', fileUrl);
  anchorTag.setAttribute('target', '_blank');
  anchorTag.setAttribute('download', fileName || `file.${type}`);
  document.body.appendChild(anchorTag);
  anchorTag.click();
  document.body.removeChild(anchorTag);
};

async function queryFn(url: string, params?: object) {
  const response = await axiosService.request({ ...params, url, data: {} });
  return response;
}

const useDownloadFile = (
  {
    url,
    fileName,
    fileType = '',
    isEnabled = true,
    onSuccess,
    onError,
    onSettled,
  }: TDownloadFileParams,
  requestParams?: AxiosRequestConfig,
) => {
  const keys = createKeys('downloadFile');

  return useCreateQuery<AxiosResponse>(
    keys.many({ url, fileName }),
    (params?: object) => queryFn(url, params),
    requestParams,
    {
      retry: false,
      enabled: isEnabled,
      onSuccess: (response: unknown) => {
        normalizeFiles(response, fileName, fileType);
        if (onSuccess) {
          onSuccess(response);
        }
      },
      onError: (error: Error) => {
        if (onError) {
          // TODO add common error treatment here, like: dispatch(showMessage(get(error, 'message'), 'error'));
          onError(error);
        }
      },
      onSettled,
    },
  );
};

const downloadFile = async (
  downloadFileParams: Omit<TDownloadFileParams, 'onSettled' | 'isEnabled'>,
  responseType: string,
  backupFileName?: string,
  contentType?: string,
) => {
  try {
    const response = await queryFn(downloadFileParams.url, {
      responseType,
      ...(contentType ? { headers: { contentType } } : {}),
    });
    normalizeFiles(
      response,
      downloadFileParams.fileName || backupFileName,
      downloadFileParams.fileType,
    );
    if (downloadFileParams.onSuccess) {
      downloadFileParams.onSuccess(response);
    }
  } catch (error) {
    if (downloadFileParams.onError) {
      downloadFileParams.onError(error);
    }
  }
};

export const downloadImage = (
  downloadFileParams: Omit<TDownloadFileParams, 'onSettled' | 'isEnabled'>,
) => downloadFile(downloadFileParams, 'blob');

export const useDownloadImageFile = (downloadFileParams: TDownloadFileParams) =>
  useDownloadFile(
    {
      ...downloadFileParams,
      fileName: downloadFileParams.fileName,
    },
    { responseType: 'blob' },
  );

export const downloadExcel = (
  downloadFileParams: Omit<TDownloadFileParams, 'onSettled' | 'isEnabled'>,
) => downloadFile(downloadFileParams, 'blob', 'file.xlsx');

export const useDownloadExcelFile = (downloadFileParams: TDownloadFileParams) =>
  useDownloadFile(
    {
      ...downloadFileParams,
      fileName:
        downloadFileParams.fileName === undefined
          ? 'file.xlsx'
          : downloadFileParams.fileName,
    },
    { responseType: 'blob' },
  );

export const downloadPdf = (
  downloadFileParams: Omit<TDownloadFileParams, 'onSettled' | 'isEnabled'>,
) =>
  downloadFile(
    downloadFileParams,
    'arraybuffer',
    'file.pdf',
    'application/pdf',
  );

export const useDownloadPdfFile = (downloadFileParams: TDownloadFileParams) =>
  useDownloadFile(
    {
      ...downloadFileParams,
      fileName:
        downloadFileParams.fileName === undefined
          ? 'file.pdf'
          : downloadFileParams.fileName,
    },
    {
      responseType: 'arraybuffer',
      headers: { contentType: 'application/pdf' },
    },
  );

export const downloadHtml = (
  downloadFileParams: Omit<TDownloadFileParams, 'onSettled' | 'isEnabled'>,
) => downloadFile(downloadFileParams, 'blob', 'file.html', 'text/html');

export const useDownloadHtmlFile = (downloadFileParams: TDownloadFileParams) =>
  useDownloadFile(
    {
      ...downloadFileParams,
      fileName:
        downloadFileParams.fileName === undefined
          ? 'file.html'
          : downloadFileParams.fileName,
      fileType: 'text/html',
    },
    { responseType: 'blob' },
  );
